import React, { useEffect, useRef, useState } from 'react';
import QrCodeWithLogo from 'qr-code-with-logo';

const QR_PAD = 5;
const QR_RADIUS = 10;
const QR_GEN_SIZE = 320;

export type StudioAdvisorQrSize = 'compact' | 'cta';

export const getStudioAdvisorQrBoxSize = (isStory?: boolean, size: StudioAdvisorQrSize = 'compact'): number => {
  if (size === 'cta') {
    return isStory ? 104 : 92;
  }
  return isStory ? 72 : 68;
};

interface StudioAdvisorQrProps {
  url: string;
  isStory?: boolean;
  size?: StudioAdvisorQrSize;
}

// Same generator + P+ logo as Profile+ (`QRCodeComponent`), without the MUI
// spinner that would flash into the Lambda screenshot.
const StudioAdvisorQr: React.FC<StudioAdvisorQrProps> = ({ url, isStory, size = 'compact' }) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [ready, setReady] = useState(false);
  const boxSize = getStudioAdvisorQrBoxSize(isStory, size);
  const inner = boxSize - QR_PAD * 2;

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    const image = imageRef.current;
    if (!url || !image) {
      return undefined;
    }

    QrCodeWithLogo.toImage({
      image,
      content: url,
      width: QR_GEN_SIZE,
      logo: {
        src: '/img/QRCode.svg',
        logoRadius: 0,
        borderRadius: 0,
        borderSize: 0,
        bgColor: 'transparent'
      },
      nodeQrCodeOptions: {
        margin: 2,
        errorCorrectionLevel: 'L'
      }
    })
      .then(() => {
        if (!cancelled) {
          setReady(true);
        }
      })
      .catch(error => {
        console.error(error);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!url) {
    return null;
  }

  return (
    <div
      style={{
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: boxSize
      }}
    >
      <div
        style={{
          width: boxSize,
          height: boxSize,
          padding: QR_PAD,
          boxSizing: 'border-box',
          background: '#ffffff',
          borderRadius: QR_RADIUS,
          lineHeight: 0
        }}
      >
        <img
          ref={imageRef}
          id="qr-code"
          alt="adviser-qr-code"
          width={inner}
          height={inner}
          style={{
            display: 'block',
            width: inner,
            height: inner,
            visibility: ready ? 'visible' : 'hidden'
          }}
        />
      </div>
    </div>
  );
};

export default StudioAdvisorQr;
